import { Avatar, Col, Input, Modal, Row, Select, Spin, Table, Tabs, message } from "antd";
import { RightOutlined, UserOutlined } from "@ant-design/icons";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import TextLink from "./TextLink";
import SuccessDeclinePopup from "reusableComponent/Popup/SuccessDeclinePopup";
import UserFloatingButton from "pages/Performance/UserFloatingButton";
import userFloatCycle from "assets/userFloatCycle.svg";
import KpiHistoryBadge from "./KpiHistoryBadge";
import { CALL_API } from "common/API";
import { STATUS_CODE } from "common/Constants";
import { useSelector } from "react-redux";
import { getAllDepartmentList, getLocations, getUserList } from "redux/selectors";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";

const Wrap = styled.div`
  .breadcrumb {
    display: flex;
    align-items: center;
  }
  .perspective-title {
    font-weight: 600;
    margin: 16px 0 8px;
  }
  .footer {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    background: #fff;
    padding: 10px;
    display: flex;
    align-items: center;
    justify-content: end;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    margin: 20px -20px 0;
  }
  .average-score-box {
    display: inline-block;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
    padding: 6px 16px;
    font-weight: 600;
  }
  .average-score-box.muted {
    color: #8c8c8c;
    font-weight: 500;
  }
  .ant-table-thead > tr > th {
    background: #C0CDE2 !important;
    font-weight: 600;
    font-size: 13px;
    color: #000000;
  }
  .ant-table-sticky-holder {
    background: #fff;
    z-index: 3;
  }
  .ant-table-tbody > tr > td {
    font-size: 13px;
    padding: 8px 12px;
  }
  .planning-group-row > td {
    background: #fafafa;
  }
`;

const { TextArea } = Input;

const SCORE_CLASSIFICATION = [
  { label: "Far Exceeds Expectations", value: 5, color: "#22c55e" },
  { label: "Exceeds Expectations (EE)", value: 4, color: "#86efac" },
  { label: "Meets Expectations (ME)", value: 3, color: "#fde047" },
  { label: "Below Expectations (BE)", value: 2, color: "#fb923c" },
  { label: "Far Below Expectations (FBE)", value: 1, color: "#ef4444" },
];

const SCORE_OPTIONS = SCORE_CLASSIFICATION.map((item) => ({
  label: item.value,
  value: item.value,
})).sort((a, b) => a.value - b.value);

const formatDate = (value) => (value ? dayjs(value).format("Do MMM YYYY") : "-");

const rowKeyOf = (perspective, sno) => `${perspective}-${sno}`;

const groupByPerspective = (perspectives = []) => {
  const grouped = {};
  perspectives.forEach((item) => {
    const key = item.perspective || "Other";
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  });
  return grouped;
};

const averageOf = (values) => {
  const nums = values.filter((val) => typeof val === "number");
  if (!nums.length) return null;
  return Math.round((nums.reduce((sum, val) => sum + val, 0) / nums.length) * 10) / 10;
};

const ReviewAppraisal = () => {
  const history = useHistory();
  const location = useLocation();
  // Opened from My Team or the HR tab - Back / Done returns there.
  const backTab = location.state?.fromTab || "myteam";
  const appraisalid = location.state?.appraisalid;

  const [appraisal, setAppraisal] = useState(null);
  const [resultsMap, setResultsMap] = useState({});
  const [selfScoreMap, setSelfScoreMap] = useState({});
  const [appraiserScoreMap, setAppraiserScoreMap] = useState({});
  const [agreedScoreMap, setAgreedScoreMap] = useState({});
  const [appraiserRemarksMap, setAppraiserRemarksMap] = useState({});
  const [softWentWellMap, setSoftWentWellMap] = useState({});
  const [softImprovedMap, setSoftImprovedMap] = useState({});
  const [softSelfScoreMap, setSoftSelfScoreMap] = useState({});
  const [softAppraiserScoreMap, setSoftAppraiserScoreMap] = useState({});
  const [softAgreedScoreMap, setSoftAgreedScoreMap] = useState({});
  const [softAppraiserCommentMap, setSoftAppraiserCommentMap] = useState({});
  const [appraisercomment, setAppraisercomment] = useState("");
  const [trainingRows, setTrainingRows] = useState([]);
  // Rollover items the appraiser flags here carry forward into the NEXT
  // contract's own "Rollover Responsibility from Previous Appraisal" table
  // (via "Copy from Previous KPI Planning") - distinct from rolloverRows
  // below, which is what THIS contract already carried in from its own
  // previous cycle.
  const [nextRolloverRows, setNextRolloverRows] = useState([{ sno: 1, objectives: "", weightage: "", comments: "" }]);
  const [showErrors, setShowErrors] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [classificationOpen, setClassificationOpen] = useState(false);
  const [kpiViewOpen, setKpiViewOpen] = useState(false);
  const [activeReviewTab, setActiveReviewTab] = useState("planning");

  useEffect(() => {
    // The sticky footer is clamped to the padding edge of this scroll
    // container, so the layout's own bottom padding always shows as a gap
    // below it - drop it to 0 while this page is mounted and restore after.
    const main = document.querySelector(".ant-layout-content");
    if (!main) return;
    const previousPaddingBottom = main.style.paddingBottom;
    main.style.paddingBottom = "0";
    return () => {
      main.style.paddingBottom = previousPaddingBottom;
    };
  }, []);

  const userList = useSelector(getUserList);
  const departmentList = useSelector(getAllDepartmentList);
  const locationList = useSelector(getLocations);
  const appraiseeUser = userList.find((user) => user.userid === appraisal?.appraiseeuserid);
  const appraiseeDepartment = departmentList.find(
    (dept) => dept.department_id === appraiseeUser?.basedepartmentid
  )?.department_name;
  const appraiseeLocation = locationList.find(
    (loc) => loc.location_id === appraiseeUser?.baselocationid
  )?.location_name;

  const fetchAppraisal = async () => {
    setLoading(true);
    try {
      const { data, code } = await CALL_API(
        `performance-kpi-management/appraisal/${appraisalid}`,
        "get"
      );
      if (code === STATUS_CODE.SUCCESS) {
        setAppraisal(data);

        const results = {};
        const selfScores = {};
        const appraiserScores = {};
        const agreedScores = {};
        const appraiserRemarks = {};
        (data?.scoring || []).forEach((item) => {
          const key = rowKeyOf(item.perspective, item.sno);
          results[key] = item.performanceresults;
          selfScores[key] = item.selfscore;
          appraiserScores[key] = item.appraiserscore;
          // Agreed score should default to the Appraiser score already on
          // record, not start empty, the first time a closed appraisal is
          // reopened to record it.
          agreedScores[key] = item.agreedscore ?? item.appraiserscore;
          appraiserRemarks[key] = item.appraiserremarks;
        });
        setResultsMap(results);
        setSelfScoreMap(selfScores);
        setAppraiserScoreMap(appraiserScores);
        setAgreedScoreMap(agreedScores);
        setAppraiserRemarksMap(appraiserRemarks);

        const wentWell = {};
        const improved = {};
        const softSelfScores = {};
        const softAppraiserScores = {};
        const softAgreedScores = {};
        const softAppraiserComments = {};
        (data?.softskillsscoring || []).forEach((item) => {
          wentWell[item.objectives] = item.wentwell;
          improved[item.objectives] = item.improved;
          softSelfScores[item.objectives] = item.selfscore;
          softAppraiserScores[item.objectives] = item.appraiserscore;
          softAgreedScores[item.objectives] = item.agreedscore ?? item.appraiserscore;
          softAppraiserComments[item.objectives] = item.appraisercomment;
        });
        setSoftWentWellMap(wentWell);
        setSoftImprovedMap(improved);
        setSoftSelfScoreMap(softSelfScores);
        setSoftAppraiserScoreMap(softAppraiserScores);
        setSoftAgreedScoreMap(softAgreedScores);
        setSoftAppraiserCommentMap(softAppraiserComments);

        setAppraisercomment(data?.appraisercomment || "");
        setTrainingRows(data?.contracttrainingdevelopmentplan || []);
      }
    } catch (error) {
      console.error("Error fetching appraisal:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (appraisalid) fetchAppraisal();
  }, [appraisalid]);

  // Once closed and reopened, Agreed score becomes the actively-editable
  // field and Appraiser score locks to a disabled dropdown (still visible,
  // no longer changeable); Appraiser's Comment stays editable in both
  // states, and the per-row Appraiser Remarks lock once closed.
  const isClosed = appraisal?.appraisalstatus === "closed";
  const canEditScores = ["yettoreview", "closed"].includes(appraisal?.appraisalstatus);
  const canEditRemarks = appraisal?.appraisalstatus === "yettoreview";

  // The 4 perspectives render as ONE continuous table (one shared sticky
  // header) - perspective titles are injected as full-width rows within the
  // same table via colSpan, rather than each perspective having its own
  // separate Table/header.
  const buildPlanningRows = (grouped) => {
    const rows = [];
    Object.entries(grouped).forEach(([perspective, items]) => {
      rows.push({ key: `group-${perspective}`, isGroupHeader: true, perspective });
      items.forEach((row) => rows.push({ ...row, key: rowKeyOf(perspective, row.sno) }));
    });
    return rows;
  };

  const fullSpanCell = (record) => (record.isGroupHeader ? { colSpan: isClosed ? 8 : 7 } : {});
  const hiddenCell = (record) => (record.isGroupHeader ? { colSpan: 0 } : {});

  const planningColumns = [
    {
      title: "S.No",
      key: "sno",
      width: 60,
      onCell: fullSpanCell,
      render: (_, record) =>
        record.isGroupHeader ? (
          <div className="perspective-title m-0">
            {record.perspective === "Rollover"
              ? "Rollover Responsibility from Previous Appraisal"
              : record.perspective}
          </div>
        ) : (
          record.sno
        ),
    },
    { title: "Objectives", dataIndex: "objectives", key: "objectives", onCell: hiddenCell },
    { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 110, onCell: hiddenCell },
    {
      title: "Performance Results / Comments",
      key: "performanceresults",
      onCell: hiddenCell,
      render: (_, record) => resultsMap[rowKeyOf(record.perspective, record.sno)] || "-",
    },
    {
      title: "Self score",
      key: "selfscore",
      width: 100,
      onCell: hiddenCell,
      render: (_, record) => selfScoreMap[rowKeyOf(record.perspective, record.sno)] ?? "-",
    },
   {
  title: "Appraiser score",
  key: "appraiserscore",
  width: 140,
  onCell: hiddenCell,
  render: (_, record) => {
    const key = rowKeyOf(record.perspective, record.sno);
    return canEditScores ? (
      <Select
        options={SCORE_OPTIONS}
        value={appraiserScoreMap[key]}
        onChange={(val) => {
          setAppraiserScoreMap((prev) => ({ ...prev, [key]: val }));
          // Agreed score tracks Appraiser score until the appraisal is
          // closed and reopened, at which point Agreed becomes its own
          // independently-editable field (see the Agreed score column
          // below) and should no longer be overwritten here.
          if (!isClosed) {
            setAgreedScoreMap((prev) => ({ ...prev, [key]: val }));
          }
        }}
        style={{ width: "100%" }}
        disabled={isClosed}
        status={
          showErrors && !isClosed && typeof appraiserScoreMap[key] !== "number"
            ? "error"
            : undefined
        }
      />
    ) : (
      appraiserScoreMap[key] ?? "-"
    );
  },
},
    // Agreed score is a follow-up step - it only becomes relevant (and is
    // only shown as a column at all) once the appraiser reopens an already
    // "closed" appraisal to record the agreed-upon score.
    ...(isClosed
      ? [
          {
            title: "Agreed score",
            key: "agreedscore",
            width: 140,
            onCell: hiddenCell,
            render: (_, record) => {
              const key = rowKeyOf(record.perspective, record.sno);
              return canEditScores ? (
                <Select
                  options={SCORE_OPTIONS}
                  value={agreedScoreMap[key]}
                  onChange={(val) => setAgreedScoreMap((prev) => ({ ...prev, [key]: val }))}
                  style={{ width: "100%" }}
                  status={showErrors && typeof agreedScoreMap[key] !== "number" ? "error" : undefined}
                />
              ) : (
                agreedScoreMap[key] ?? "-"
              );
            },
          },
        ]
      : []),
    {
      title: "Appraiser Remarks",
      key: "appraiserremarks",
      onCell: hiddenCell,
      render: (_, record) => {
        const key = rowKeyOf(record.perspective, record.sno);
        return canEditRemarks ? (
          <TextArea
            autoSize={{ minRows: 1, maxRows: 4 }}
            value={appraiserRemarksMap[key]}
            onChange={(e) =>
              setAppraiserRemarksMap((prev) => ({ ...prev, [key]: e.target.value }))
            }
            status={showErrors && !String(appraiserRemarksMap[key] || "").trim() ? "error" : undefined}
          />
        ) : (
          appraiserRemarksMap[key] || "-"
        );
      },
    },
  ];

  const softSkillsColumns = [
    { title: "Objectives", dataIndex: "objectives", key: "objectives" },
    { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 110 },
    {
      title: "What went well/ Major achievement",
      key: "wentwell",
      render: (_, row) => softWentWellMap[row.objectives] || "-",
    },
    {
      title: "What can be improved / Challenges",
      key: "improved",
      render: (_, row) => softImprovedMap[row.objectives] || "-",
    },
    {
      title: "Self score",
      key: "selfscore",
      width: 100,
      render: (_, row) => softSelfScoreMap[row.objectives] ?? "-",
    },
    {
  title: "Appraiser score",
  key: "appraiserscore",
  width: 140,
  render: (_, row) =>
    canEditScores ? (
      <Select
        options={SCORE_OPTIONS}
        value={softAppraiserScoreMap[row.objectives]}
        onChange={(val) => {
          setSoftAppraiserScoreMap((prev) => ({ ...prev, [row.objectives]: val }));
          if (!isClosed) {
            setSoftAgreedScoreMap((prev) => ({ ...prev, [row.objectives]: val }));
          }
        }}
        style={{ width: "100%" }}
        disabled={isClosed}
        status={
          showErrors && !isClosed && typeof softAppraiserScoreMap[row.objectives] !== "number"
            ? "error"
            : undefined
        }
      />
    ) : (
      softAppraiserScoreMap[row.objectives] ?? "-"
    ),
},
    ...(isClosed
      ? [
          {
            title: "Agreed score",
            key: "agreedscore",
            width: 140,
            render: (_, row) =>
              canEditScores ? (
                <Select
                  options={SCORE_OPTIONS}
                  value={softAgreedScoreMap[row.objectives]}
                  onChange={(val) =>
                    setSoftAgreedScoreMap((prev) => ({ ...prev, [row.objectives]: val }))
                  }
                  style={{ width: "100%" }}
                  status={
                    showErrors && typeof softAgreedScoreMap[row.objectives] !== "number"
                      ? "error"
                      : undefined
                  }
                />
              ) : (
                softAgreedScoreMap[row.objectives] ?? "-"
              ),
          },
        ]
      : []),
    {
      title: "Appraiser Comment",
      key: "appraisercomment",
      render: (_, row) =>
        canEditRemarks ? (
          <TextArea
            autoSize={{ minRows: 1, maxRows: 4 }}
            value={softAppraiserCommentMap[row.objectives]}
            onChange={(e) =>
              setSoftAppraiserCommentMap((prev) => ({ ...prev, [row.objectives]: e.target.value }))
            }
            status={
              showErrors && !String(softAppraiserCommentMap[row.objectives] || "").trim()
                ? "error"
                : undefined
            }
          />
        ) : (
          softAppraiserCommentMap[row.objectives] || "-"
        ),
    },
  ];

  const updateTrainingComment = (index, value) => {
    setTrainingRows((prev) => {
      const rows = [...prev];
      rows[index] = { ...rows[index], appraisertrainingcomment: value };
      return rows;
    });
  };

  const updateTrainingTaken = (index, value) => {
    setTrainingRows((prev) => {
      const rows = [...prev];
      rows[index] = { ...rows[index], taken: value };
      return rows;
    });
  };

  const trainingColumns = [
    { title: "S.No", dataIndex: "sno", key: "sno", width: 60 },
    { title: "Planned areas for strengthening", dataIndex: "skill", key: "skill" },
    { title: "Method of development", dataIndex: "method", key: "method" },
    { title: "Planned / Expected quarter", dataIndex: "quarter", key: "quarter", width: 160 },
    {
      title: "Development / training taken (also indicate method & place)",
      key: "taken",
      render: (_, row, index) =>
        canEditScores ? (
          <TextArea
            autoSize={{ minRows: 1, maxRows: 4 }}
            value={row.taken}
            onChange={(e) => updateTrainingTaken(index, e.target.value)}
          />
        ) : (
          row.taken || "-"
        ),
    },
    {
      // Optional - no score, just a free-text note from the appraiser.
      title: "Appraiser Comment",
      key: "appraisertrainingcomment",
      render: (_, row, index) =>
        canEditScores ? (
          <TextArea
            autoSize={{ minRows: 1, maxRows: 4 }}
            value={row.appraisertrainingcomment}
            onChange={(e) => updateTrainingComment(index, e.target.value)}
          />
        ) : (
          row.appraisertrainingcomment || "-"
        ),
    },
  ];

  const grouped = groupByPerspective(
    (appraisal?.contractperspectives || []).filter(
      (item) => item.perspective !== "Individual (Soft Skills)"
    )
  );
  const rolloverRows = appraisal?.contractrolloverresponsibilities || [];
  const combinedGrouped = { ...grouped };
  if (rolloverRows.length > 0) {
    combinedGrouped.Rollover = rolloverRows.map((row) => ({ ...row, perspective: "Rollover" }));
  }

  // "KPI Planning & Progress Reviews" modal (opened via the floating button) -
  // same read-only Planning/Rollover view AttendContract.js shows, projecting
  // either the original plan or a completed checkpoint's achievements /
  // corrective actions depending on the selected tab.
  const isKpiModalReviewTab = activeReviewTab !== "planning";
  const kpiModalColSpan = isKpiModalReviewTab ? 6 : 5;
  const kpiModalFullSpanCell = (record) => (record.isGroupHeader ? { colSpan: kpiModalColSpan } : {});
  const kpiModalHiddenCell = (record) => (record.isGroupHeader ? { colSpan: 0 } : {});
  const kpiModalColumns = [
    {
      title: "S.No",
      key: "sno",
      width: 60,
      onCell: kpiModalFullSpanCell,
      render: (_, record) =>
        record.isGroupHeader ? (
          <div className="perspective-title m-0">
            {record.perspective === "Rollover"
              ? "Rollover Responsibility from Previous Appraisal"
              : record.perspective}
          </div>
        ) : (
          record.sno
        ),
    },
    {
      title: "Measurement Dimensions",
      dataIndex: "measurementdimension",
      key: "measurementdimension",
      onCell: kpiModalHiddenCell,
    },
    { title: "Objectives", dataIndex: "objectives", key: "objectives", onCell: kpiModalHiddenCell },
    { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 110, onCell: kpiModalHiddenCell },
    ...(isKpiModalReviewTab
      ? [
          {
            title: "Achievements / Progress to date",
            key: "achievements",
            onCell: kpiModalHiddenCell,
            render: (_, record) => record.progressupdates?.[activeReviewTab]?.achievements || "-",
          },
          {
            title: "Emphasis / Corrective actions",
            key: "correctiveactions",
            onCell: kpiModalHiddenCell,
            render: (_, record) => record.progressupdates?.[activeReviewTab]?.correctiveactions || "-",
          },
        ]
      : [
          {
            title: "Planning Assumptions / Comments",
            dataIndex: "comments",
            key: "comments",
            onCell: kpiModalHiddenCell,
            render: (val) => val || "-",
          },
        ]),
  ];
  const kpiModalGrouped = groupByPerspective(appraisal?.contractperspectives);
  const kpiModalRolloverRows = appraisal?.contractrolloverresponsibilities || [];
  if (kpiModalRolloverRows.length > 0) {
    kpiModalGrouped.Rollover = kpiModalRolloverRows.map((row) => ({ ...row, perspective: "Rollover" }));
  }
  const kpiModalTabItems = [
    { key: "planning", label: "KPI Planning" },
    ...(appraisal?.contractprogressreviewdates || [])
      .filter((item) => item.completed)
      .map((item) => ({ key: item.label, label: `${item.label} Review` })),
  ];

  const selfAverage = averageOf(
    Object.entries(selfScoreMap)
      .filter(([key]) => !key.startsWith("Individual (Soft Skills)-"))
      .map(([, value]) => value)
  );
  const appraiserAverage = averageOf(
    Object.entries(appraiserScoreMap)
      .filter(([key]) => !key.startsWith("Individual (Soft Skills)-"))
      .map(([, value]) => value)
  );
  const agreedAverage = averageOf(
    Object.entries(agreedScoreMap)
      .filter(([key]) => !key.startsWith("Individual (Soft Skills)-"))
      .map(([, value]) => value)
  );
  const softSelfAverage = averageOf(Object.values(softSelfScoreMap));
  const softAppraiserAverage = averageOf(Object.values(softAppraiserScoreMap));
  const softAgreedAverage = averageOf(Object.values(softAgreedScoreMap));

  const buildScoring = () => {
    const keys = new Set([
      ...Object.keys(resultsMap),
      ...Object.keys(selfScoreMap),
      ...Object.keys(appraiserScoreMap),
      ...Object.keys(agreedScoreMap),
      ...Object.keys(appraiserRemarksMap),
    ]);
    return Array.from(keys).map((key) => {
      const [perspective, sno] = key.split("-");
      return {
        perspective,
        sno,
        performanceresults: resultsMap[key],
        selfscore: selfScoreMap[key],
        appraiserscore: appraiserScoreMap[key],
        agreedscore: agreedScoreMap[key],
        appraiserremarks: appraiserRemarksMap[key],
      };
    });
  };

  const buildSoftSkillsScoring = () => {
    const keys = new Set([
      ...Object.keys(softWentWellMap),
      ...Object.keys(softImprovedMap),
      ...Object.keys(softSelfScoreMap),
      ...Object.keys(softAppraiserScoreMap),
      ...Object.keys(softAgreedScoreMap),
      ...Object.keys(softAppraiserCommentMap),
    ]);
    return Array.from(keys).map((objectives) => ({
      objectives,
      wentwell: softWentWellMap[objectives],
      improved: softImprovedMap[objectives],
      selfscore: softSelfScoreMap[objectives],
      appraiserscore: softAppraiserScoreMap[objectives],
      agreedscore: softAgreedScoreMap[objectives],
      appraisercomment: softAppraiserCommentMap[objectives],
    }));
  };

  const submitReview = async (isdraft) => {
    setSubmitLoading(true);
    try {
      const { code } = await CALL_API(
        `performance-kpi-management/review/${appraisalid}`,
        "patch",
        {
          scoring: buildScoring(),
          softskillsscoring: buildSoftSkillsScoring(),
          appraisercomment,
          trainingdevelopmentplan: trainingRows,
          isdraft,
        }
      );
      if (code === STATUS_CODE.SUCCESS) {
        setPopupOpen(true);
        setTimeout(() => {
          setPopupOpen(false);
          history.push("/performance-kpi", { id: "appraisal", subTab: backTab });
        }, 1500);
      }
    } catch (error) {
      console.error("Error submitting review:", error);
    } finally {
      setSubmitLoading(false);
    }
  };

  const onSaveDraft = () => submitReview(true);

  const validateCloseForm = () => {
    const planningRows = Object.values(combinedGrouped).flat();
    const planningIncomplete = planningRows.some((row) => {
      const key = rowKeyOf(row.perspective, row.sno);
      return (
        typeof appraiserScoreMap[key] !== "number" ||
        !String(appraiserRemarksMap[key] || "").trim()
      );
    });
    const softSkillsRows = appraisal?.contractsoftskillsassessment || [];
    const softSkillsIncomplete = softSkillsRows.some(
      (row) =>
        typeof softAppraiserScoreMap[row.objectives] !== "number" ||
        !String(softAppraiserCommentMap[row.objectives] || "").trim()
    );
    if (planningIncomplete || softSkillsIncomplete || !String(appraisercomment || "").trim()) {
      setShowErrors(true);
      message.error("Please fill in all fields before closing the review.");
      return false;
    }
    setShowErrors(false);
    return true;
  };

  const validateSaveChangesForm = () => {
    const planningRows = Object.values(combinedGrouped).flat();
    const planningIncomplete = planningRows.some(
      (row) => typeof agreedScoreMap[rowKeyOf(row.perspective, row.sno)] !== "number"
    );
    const softSkillsRows = appraisal?.contractsoftskillsassessment || [];
    const softSkillsIncomplete = softSkillsRows.some(
      (row) => typeof softAgreedScoreMap[row.objectives] !== "number"
    );
    if (planningIncomplete || softSkillsIncomplete) {
      setShowErrors(true);
      message.error("Please fill in the agreed score for all fields before saving.");
      return false;
    }
    setShowErrors(false);
    return true;
  };

  const onSaveChanges = () => {
    if (!validateSaveChangesForm()) return;
    Modal.confirm({
      title: "The Employee will be notified with the appraisal changes you made.",
      content: "Are you sure you want to continue with the changes?",
      icon: null,
      centered: true,
      okText: "Yes",
      cancelText: "No",
      okButtonProps: { type: "primary" },
      onOk: () => submitReview(false),
    });
  };

  const onClose = () => {
    if (!validateCloseForm()) return;
    Modal.confirm({
      title: "Close this review? This can't be changed afterwards.",
      icon: null,
      centered: true,
      okText: "Yes, Close",
      cancelText: "No, Cancel",
      okButtonProps: { type: "primary" },
      onOk: () => submitReview(false),
    });
  };

  return (
    <Wrap>
      <Spin spinning={loading}>
        <div className="breadcrumb mb-3">
          <p
            className="lite-text p-0 m-0"
            style={{ cursor: "pointer" }}
            onClick={() => history.push("/performance-kpi", { id: "appraisal", subTab: backTab })}
          >
            Performance Management
          </p>
          &nbsp;
          <RightOutlined className="mt-1" />
          &nbsp;
          <p className="m-0 p-0">{appraisal?.fullname || "-"}</p>
        </div>

        <h6>{isClosed ? "Closed" : "Reviewing to Close"}</h6>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="perspective-title m-0">Planning</div>
          <TextLink onClick={() => setClassificationOpen(true)}>Score Classification</TextLink>
        </div>
        <p className="lite-text">
          Objectives or other developmental activities to be undertaken by Appraisee during the
          appraisal period.
        </p>

        <Table
          columns={planningColumns}
          dataSource={buildPlanningRows(combinedGrouped)}
          pagination={false}
          rowKey={(row) => row.key}
          sticky={{ getContainer: () => document.querySelector(".ant-layout-content") }}
          rowClassName={(record) => (record.isGroupHeader ? "planning-group-row" : "")}
        />

        {(selfAverage !== null || appraiserAverage !== null || (isClosed && agreedAverage !== null)) && (
          <div className="text-end mb-2">
            {selfAverage !== null && (
              <span className="average-score-box muted me-2">Average Score: {selfAverage}</span>
            )}
            {appraiserAverage !== null && (
              <span className="average-score-box me-2">Average Score: {appraiserAverage}</span>
            )}
            {isClosed && agreedAverage !== null && (
              <span className="average-score-box">Agreed Average: {agreedAverage}</span>
            )}
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center mt-4">
          <div className="perspective-title m-0">Individual (soft skills) Assessment</div>
          <TextLink onClick={() => setClassificationOpen(true)}>Score Classification</TextLink>
        </div>
        <Table
          columns={softSkillsColumns}
          dataSource={appraisal?.contractsoftskillsassessment || []}
          pagination={false}
          rowKey={(record) => `softskills-${record.objectives}`}
        />

        {(softSelfAverage !== null || softAppraiserAverage !== null || (isClosed && softAgreedAverage !== null)) && (
          <div className="text-end mb-2">
            {softSelfAverage !== null && (
              <span className="average-score-box muted me-2">Average Score: {softSelfAverage}</span>
            )}
            {softAppraiserAverage !== null && (
              <span className="average-score-box me-2">Average Score: {softAppraiserAverage}</span>
            )}
            {isClosed && softAgreedAverage !== null && (
              <span className="average-score-box">Agreed Average: {softAgreedAverage}</span>
            )}
          </div>
        )}

        {trainingRows.length > 0 && (
          <>
            <div className="perspective-title mt-4">Training & Development Plan</div>
            <p className="lite-text">
              Based on the strengths and limitations identified in the previous review, indicate the
              work experience and/or training suggested during the plan period.
            </p>
            <Table
              columns={trainingColumns}
              dataSource={trainingRows}
              pagination={false}
              rowKey={(record) => `training-${record.sno}`}
            />
          </>
        )}

        <div className="perspective-title mt-4">Appraisee's Comment</div>
        <p>{appraisal?.appraiseecomment || "-"}</p>

        <div className="perspective-title mt-4">Appraiser's Comment</div>
        {canEditScores ? (
          <TextArea
            autoSize={{ minRows: 2, maxRows: 6 }}
            value={appraisercomment}
            onChange={(e) => setAppraisercomment(e.target.value)}
            status={showErrors && !isClosed && !String(appraisercomment || "").trim() ? "error" : undefined}
          />
        ) : (
          <p>{appraisercomment || "-"}</p>
        )}

        <UserFloatingButton setIsViewKpi={setKpiViewOpen} bottompossition="90px" icon={userFloatCycle} />

        <div className="footer">
          {appraisal?.contractid && <KpiHistoryBadge contractid={appraisal.contractid} />}
          {canEditScores && isClosed && (
            <>
              <ButtonOutlined onClick={() => history.push("/performance-kpi", { id: "appraisal", subTab: backTab })}>
                Cancel
              </ButtonOutlined>
              <ButtonFilled type="primary" loading={submitLoading} onClick={onSaveChanges} className="ms-2">
                Save Changes
              </ButtonFilled>
            </>
          )}
          {canEditScores && !isClosed && (
            <>
              <ButtonOutlined onClick={() => history.push("/performance-kpi", { id: "appraisal", subTab: backTab })}>
                Cancel
              </ButtonOutlined>
              <ButtonOutlined loading={submitLoading} onClick={onSaveDraft} className="mx-2">
                Save as Draft
              </ButtonOutlined>
              <ButtonFilled type="primary" loading={submitLoading} onClick={onClose}>
                Close
              </ButtonFilled>
            </>
          )}
        </div>
      </Spin>

      <SuccessDeclinePopup
        open={popupOpen}
        setOpen={setPopupOpen}
        type="success"
        message="Reviewed"
      />

      <Modal
        title="Score Classification"
        open={classificationOpen}
        onCancel={() => setClassificationOpen(false)}
        footer={null}
        centered
      >
        {SCORE_CLASSIFICATION.map((item) => (
          <div
            key={item.value}
            style={{ background: item.color, padding: "12px 16px", marginBottom: 4 }}
          >
            {item.value} - {item.label}
          </div>
        ))}
      </Modal>

      <Modal
        title="KPI Planning & Progress Reviews"
        open={kpiViewOpen}
        onCancel={() => setKpiViewOpen(false)}
        footer={null}
        width={1100}
        styles={{ body: { maxHeight: "75vh", overflowY: "auto" } }}
        centered
      >
        <div style={{ background: "#eef1fb", borderRadius: 8, padding: "16px 24px", marginBottom: 16 }}>
          <Row gutter={16} align="middle">
            <Col flex="0 0 auto">
              <Avatar size={64} src={appraiseeUser?.profileurl} icon={<UserOutlined />} />
            </Col>
            <Col flex="0 0 220px">
              <div className="text-bold">{appraisal?.fullname || "-"}</div>
              <div className="lite-text">{appraiseeUser?.jobtitle || "-"}</div>
            </Col>
            <Col flex="1 1 0">
              <div className="lite-text">
                Employee ID: <span className="text-bold">{appraisal?.employeeid || "-"}</span>
              </div>
              <div className="lite-text mt-1">
                Location: <span className="text-bold">{appraiseeLocation || "-"}</span>
              </div>
            </Col>
            <Col flex="1 1 0">
              <div className="lite-text">
                Department: <span className="text-bold">{appraiseeDepartment || "-"}</span>
              </div>
              <div className="lite-text mt-1">
                Hired Date: <span className="text-bold">{formatDate(appraiseeUser?.hireddate)}</span>
              </div>
            </Col>
          </Row>
        </div>

        <Row gutter={[16, 16]} className="mb-3">
          <Col xs={24} md={12}>
            <div className="lite-text">Start date to attend appraisal</div>
            <div className="text-bold">{formatDate(appraisal?.startdate)}</div>
          </Col>
          <Col xs={24} md={12}>
            <div className="lite-text">KPI defined by</div>
            <div className="text-bold">
              {appraisal?.kpidefinedby === "Self"
                ? `Appraisee : ${appraisal?.fullname || "-"}`
                : `Appraiser : ${appraisal?.kpidefinedby || "-"}`}
            </div>
          </Col>
          <Col xs={24} md={12}>
            <div className="lite-text">Period</div>
            <div className="text-bold">{appraisal?.period || "-"}</div>
          </Col>
          <Col xs={24} md={12}>
            <div className="lite-text">Progress Review</div>
            {(appraisal?.contractprogressreviewdates || []).map((item) => (
              <div className="text-bold" key={item.label}>
                {item.label} – {formatDate(item.date)}
              </div>
            ))}
          </Col>
        </Row>

        <Tabs activeKey={activeReviewTab} onChange={setActiveReviewTab} items={kpiModalTabItems} />

        <div className="perspective-title">Planning</div>
        <p className="lite-text">
          Objectives or other developmental activities to be undertaken by Appraisee during the
          appraisal period.
        </p>
        <Table
          columns={kpiModalColumns}
          dataSource={buildPlanningRows(kpiModalGrouped)}
          pagination={false}
          rowKey={(row) => row.key}
          sticky={{ getContainer: () => document.querySelector(".ant-layout-content") }}
          rowClassName={(record) => (record.isGroupHeader ? "planning-group-row" : "")}
        />

        {(appraisal?.contractsoftskillsassessment || []).length > 0 && (
          <>
            <div className="perspective-title mt-4">Individual (soft skills) Assessment</div>
            <Table
              columns={[
                { title: "Objectives", dataIndex: "objectives", key: "objectives" },
                { title: "Weightage (%)", dataIndex: "weightage", key: "weightage", width: 110 },
              ]}
              dataSource={appraisal.contractsoftskillsassessment}
              pagination={false}
              rowKey={(row) => `modal-softskills-${row.objectives}`}
            />
          </>
        )}

        {(appraisal?.contracttrainingdevelopmentplan || []).length > 0 && (
          <>
            <div className="perspective-title mt-4">Training & Development Plan</div>
            <Table
              columns={trainingColumns}
              dataSource={appraisal.contracttrainingdevelopmentplan}
              pagination={false}
              rowKey={(row) => `modal-training-${row.sno}`}
            />
          </>
        )}

        <div className="perspective-title mt-4">Appraisee's Comment</div>
        <p>{appraisal?.appraiseecomment || "-"}</p>

        <div className="perspective-title mt-4">Appraiser's Comment</div>
        <p>{appraisercomment || "-"}</p>
      </Modal>
    </Wrap>
  );
};

export default ReviewAppraisal;
